import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import InvitesPage from "@/features/team/pages/InvitesPage";
import TeamManagementPage from "@/features/team/pages/TeamManagementPage";
import BillingPage from "@/features/billing/pages/BillingPage";
import { supabase } from "@/integrations/supabase/client";
import { apiClient } from "@/shared/services/apiClient";
import { API_ROUTES } from "@/shared/services/apiRoutes";
import { validatePassword } from "@/lib/validation";
import {
  ArrowLeft,
  Camera,
  Eye,
  EyeOff,
  Lock,
  Bell,
  Shield,
  Save,
  MessageCircle,
  Search,
  Reply,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  CreditCard,
  Crown,
  FileText,
  Mail,
  Calendar,
  DollarSign,
  AlertTriangle,
  Plus,
  Download,
  XCircle,
  Settings,
  Users,
  HelpCircle,
  Plug
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Switch } from "@/shared/components/ui/switch";
import { Separator } from "@/shared/components/ui/separator";
import { Badge } from "@/shared/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { useToast } from "@/shared/hooks/use-toast";
import { useSettings } from "@/shared/contexts/SettingsContext";
import { useDeviceTracking } from "@/shared/hooks/useDeviceTracking";
import { useSubscription } from "@/features/billing/contexts/SubscriptionContext";
import { useAuth } from "@/shared/contexts/AuthContext";
import GoogleCalendarIntegration from "@/features/integrations/GoogleCalendarIntegration";

interface Message {
  id: string;
  subject: string;
  preview: string;
  content: string;
  from: string;
  timestamp: string;
  status: 'unread' | 'read' | 'replied';
  ticketId?: string;
  priority: 'low' | 'medium' | 'high';
}

// Mock data for messages
const mockMessages: Message[] = [
  {
    id: "1",
    subject: "Support Ticket Response - Video Call Issue",
    preview: "Thank you for reporting the video call connectivity issue. Our team has identified the problem and...",
    content: "Thank you for reporting the video call connectivity issue. Our team has identified the problem and implemented a fix. The issue was related to WebRTC connection handling in certain network configurations. We've deployed an update that should resolve this issue. Please try initiating a video call again and let us know if you continue to experience any problems.",
    from: "Support Team",
    timestamp: "2024-01-15T10:30:00Z",
    status: "unread",
    ticketId: "TICKET-001",
    priority: "high"
  },
  {
    id: "2",
    subject: "Feature Request Update - Calendar Integration",
    preview: "We're excited to let you know that the calendar integration feature you requested is now in development...",
    content: "We're excited to let you know that the calendar integration feature you requested is now in development. Our development team has begun working on integrating with popular calendar services including Google Calendar, Outlook, and Apple Calendar. We expect this feature to be available in the next major release scheduled for March 2024. We'll keep you updated on the progress.",
    from: "Product Team",
    timestamp: "2024-01-14T14:22:00Z",
    status: "read",
    priority: "medium"
  },
  {
    id: "3",
    subject: "Account Verification Completed",
    preview: "Your account verification has been successfully completed. You now have access to all premium features...",
    content: "Your account verification has been successfully completed. You now have access to all premium features including unlimited video calls, advanced reporting, and priority support. Thank you for choosing our platform. If you have any questions about your new features, please don't hesitate to reach out.",
    from: "Account Team",
    timestamp: "2024-01-13T09:15:00Z",
    status: "replied",
    priority: "low"
  }
];

const TIMEZONE_OPTIONS = [
  "EST (UTC-5)",
  "CST (UTC-6)",
  "MST (UTC-7)",
  "PST (UTC-8)",
  "AKST (UTC-9)",
  "HST (UTC-10)",
  "UTC",
  "GMT (UTC+0)",
  "CET (UTC+1)",
  "IST (UTC+5:30)"
];

export default function ProfilePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const {
    setUserTimezone,
    emailNotifications,
    setEmailNotifications,
    pushNotifications,
    setPushNotifications,
    smsNotifications,
    setSmsNotifications,
    productUpdates,
    setProductUpdates,
    securityAlerts,
    setSecurityAlerts,
    marketingEmails,
    setMarketingEmails
  } = useSettings();
  const { getUserDevices } = useDeviceTracking();
  const { usage: subscriptionUsage, loading: subscriptionLoading } = useSubscription();
  const { refreshUserData } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isGoogleAuth, setIsGoogleAuth] = useState(false); // Track if user signed in with Google

  // Messages state
  const [messages, setMessages] = useState<Message[]>(mockMessages);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [replyText, setReplyText] = useState("");

  // Billing state and data
  const [billingSearchTerm, setBillingSearchTerm] = useState("");

  // ✅ REMOVED: Mock subscription data - BillingPage component now uses real data from SubscriptionContext
  // ProfilePage itself doesn't display subscription info - it's handled by the BillingPage component

  // Billing information - will be loaded from user data
  const [billingInfo, setBillingInfo] = useState({
    name: "",
    email: "",
    company: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: ""
  });
  
  // Dialog state for updating billing info
  const [showBillingDialog, setShowBillingDialog] = useState(false);
  const [billingForm, setBillingForm] = useState({
    name: "",
    email: "",
    company: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: ""
  });

  // Mock payment method
  const [paymentMethod] = useState({
    hasPayment: false,
    cardType: null,
    last4: null,
    expiryMonth: null,
    expiryYear: null
  });

  // Mock invoice history
  const invoiceHistory = [
    {
      id: "INV-001",
      date: "2024-07-15",
      amount: 1600.00,
      status: "paid",
      description: "Annual subscription - wlkthru Pro",
      downloadUrl: "#"
    },
    {
      id: "INV-002", 
      date: "2023-07-15",
      amount: 1500.00,
      status: "paid",
      description: "Annual subscription - wlkthru Pro v1",
      downloadUrl: "#"
    },
    {
      id: "INV-003",
      date: "2023-06-15",
      amount: 150.00,
      status: "failed",
      description: "Additional storage upgrade",
      downloadUrl: null
    }
  ];

  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [personalDetails, setPersonalDetails] = useState({
    companyName: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    roleJobTitle: "",
    location: "",
    website: "",
    timezone: "EST (UTC-5)"
  });

  // Avatar state
  const [avatarUrl, setAvatarUrl] = useState<string>("");
  const [initialAvatarUrl, setInitialAvatarUrl] = useState<string>("");
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
  const [pendingAvatarPreview, setPendingAvatarPreview] = useState<string>("");

  // Load user data from backend API on component mount
  useEffect(() => {
    const getUserData = async () => {
      try {
        console.log('[ProfilePage] Fetching user data from backend...');

        // Check if user signed in with Google
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser) {
          // Check app_metadata for provider info
          const identities = authUser.identities || [];
          const hasGoogleIdentity = identities.some((identity: any) => identity.provider === 'google');
          setIsGoogleAuth(hasGoogleIdentity);
          console.log('[ProfilePage] User auth provider:', hasGoogleIdentity ? 'Google' : 'Email/Password');
        }

        // Call backend /user/me endpoint (returns profile + company + subscription)
        const response = await apiClient.get<{
          ok: boolean;
          user: any;
          company: any;
          subscription: any;
          userRole: any;
        }>(API_ROUTES.USER_ME);

        if (!response.ok || !response.user) {
          console.error('[ProfilePage] No user data returned from backend');
          // Try to initialize company if no data
          try {
            await apiClient.post(API_ROUTES.USER_INITIALIZE_COMPANY);
            // Retry loading data
            const retryResponse = await apiClient.get(API_ROUTES.USER_ME);
            if (retryResponse.ok && retryResponse.user) {
              processUserData(retryResponse);
            }
          } catch (initError) {
            console.error('[ProfilePage] Failed to initialize company:', initError);
          }
          return;
        }

        processUserData(response);
      } catch (error) {
        console.error('[ProfilePage] Error loading user data:', error);
        toast({
          title: "Error",
          description: "Failed to load profile data. Please refresh the page.",
          variant: "destructive",
        });
      }
    };

    // Helper function to process user data response
    const processUserData = (response: any) => {
      const { user: profile, company, userRole } = response;

      console.log('[ProfilePage] User data loaded:', { profile, company, userRole });

      // Check if user is company admin (owner)
      // User is admin if they have role 'admin' or 'owner', or if company doesn't exist yet
      const isAdmin = userRole?.role === 'admin' || userRole?.role === 'owner' || !company;
      setIsCompanyAdmin(isAdmin);

      // Use actual first_name and last_name fields from database
      // Fallback to parsing full_name only if individual fields are missing
      let firstName = profile.first_name || "";
      let lastName = profile.last_name || "";

      // Fallback: If first/last name are empty, try splitting full_name
      if (!firstName && !lastName && profile.name) {
        const nameParts = profile.name.trim().split(/\s+/);
        firstName = nameParts[0] || "";
        lastName = nameParts.slice(1).join(" ") || "";
      }

      // Set personal details
      setPersonalDetails({
        companyName: company?.company_name || profile.company_name || "",
        firstName: firstName,
        lastName: lastName,
        phoneNumber: profile.phone || "",
        roleJobTitle: profile.role_job_title || "",
        location: (profile.city && profile.state) ? `${profile.city}, ${profile.state}` : "",
        website: profile.website || "",
        timezone: profile.timezone || "EST (UTC-5)"
      });

      // Update settings context with timezone
      setUserTimezone(profile.timezone || "EST (UTC-5)");

      // Set billing info
      setBillingInfo({
        name: profile.name || "",
        email: profile.email || "",
        company: company?.company_name || profile.company_name || "",
        address: "",
        city: profile.city || "",
        state: profile.state || "",
        zipCode: profile.zip_code || "",
        country: profile.country || ""
      });

      setBillingForm({
        name: profile.name || "",
        email: profile.email || "",
        company: company?.company_name || profile.company_name || "",
        address: "",
        city: profile.city || "",
        state: profile.state || "",
        zipCode: profile.zip_code || "",
        country: profile.country || ""
      });

      // Set avatar URL from profile
      if (profile.avatar_url) {
        setAvatarUrl(profile.avatar_url);
        setInitialAvatarUrl(profile.avatar_url);
      }
    };

    getUserData();
  }, [setUserTimezone, toast]);

  // Function to handle avatar file selection (doesn't save yet)
  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast({
        title: "File too large",
        description: "Please choose an image smaller than 5MB.",
        variant: "destructive"
      });
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please choose an image file (JPG, PNG, GIF).",
        variant: "destructive"
      });
      return;
    }

    // Store the file for later upload when Save is clicked
    setPendingAvatarFile(file);

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setPendingAvatarPreview(previewUrl);
    setAvatarUrl(previewUrl);

    toast({
      title: "Photo selected",
      description: "Click 'Save Changes' to update your profile photo.",
    });
  };

  // Function to get user initials for fallback
  const getUserInitials = () => {
    const first = personalDetails.firstName?.trim()[0] || '';
    const last = personalDetails.lastName?.trim()[0] || '';
    if (first && last) return (first + last).toUpperCase();
    if (first) return first.toUpperCase();
    return "U";
  };

  // ✅ Notification preferences now managed by SettingsContext (loaded from backend)

  const [security, setSecurity] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    twoFactorEnabled: false,
    loginNotifications: true
  });
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isSavingBilling, setIsSavingBilling] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [pendingInvitesCount, setPendingInvitesCount] = useState<number>(0);

  // Initial values for change detection
  const [initialPersonalDetails, setInitialPersonalDetails] = useState({
    companyName: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    roleJobTitle: "",
    location: "",
    website: "",
    timezone: "EST (UTC-5)"
  });

  // Update initial values when data loads - only once when component mounts
  useEffect(() => {
    if (personalDetails.firstName && !initialPersonalDetails.firstName) {
      setInitialPersonalDetails(personalDetails);
    }
  }, [personalDetails.firstName]); // Only update when firstName is first loaded

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (pendingAvatarPreview) {
        URL.revokeObjectURL(pendingAvatarPreview);
      }
    };
  }, [pendingAvatarPreview]);

  // Fetch pending invites count from backend
  useEffect(() => {
    const fetchInviteCount = async () => {
      try {
        const response = await apiClient.get<{ ok: boolean; count: number }>(
          API_ROUTES.USER_INVITES_COUNT
        );

        if (response.ok) {
          setPendingInvitesCount(response.count);
        } else {
          setPendingInvitesCount(0);
        }
      } catch (error) {
        console.error('[ProfilePage] Error fetching invite count:', error);
        setPendingInvitesCount(0);
      }
    };
    fetchInviteCount();
  }, []);

  // Change detection functions
  const hasPersonalDetailsChanged = () => {
    const personalDataChanged = JSON.stringify(personalDetails) !== JSON.stringify(initialPersonalDetails);
    const avatarChanged = pendingAvatarFile !== null || avatarUrl !== initialAvatarUrl;
    return personalDataChanged || avatarChanged;
  };

  // ✅ Notification changes are auto-saved via SettingsContext setters

  // Add beforeunload listener to prevent accidental data loss
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasPersonalDetailsChanged()) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasPersonalDetailsChanged]);

  // Messages functions
  const filteredMessages = messages.filter(message =>
    message.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusIcon = (status: Message['status']) => {
    switch (status) {
      case 'unread':
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      case 'read':
        return <CheckCircle className="h-4 w-4 text-blue-500" />;
      case 'replied':
        return <Reply className="h-4 w-4 text-green-500" />;
      default:
        return <MessageCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority: Message['priority']) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleMessageClick = (message: Message) => {
    setSelectedMessage(message);
    // Mark as read
    setMessages(prev => 
      prev.map(msg => 
        msg.id === message.id ? { ...msg, status: 'read' } : msg
      )
    );
  };

  const handleReply = () => {
    if (replyText.trim() && selectedMessage) {
      // Here you would typically send the reply to your backend
      console.log('Sending reply:', replyText);
      
      // Update message status
      setMessages(prev => 
        prev.map(msg => 
          msg.id === selectedMessage.id ? { ...msg, status: 'replied' } : msg
        )
      );
      
      setReplyText("");
      setSelectedMessage(null);
    }
  };

  const unreadCount = messages.filter(msg => msg.status === 'unread').length;

  // Billing utility functions
  const getInvoiceStatusIcon = (status: string) => {
    switch (status) {
      case "paid":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-red-600" />;
      case "pending":
        return <Clock className="h-4 w-4 text-yellow-600" />;
      default:
        return null;
    }
  };

  const getInvoiceStatusBadge = (status: string) => {
    const variants = {
      paid: "default",
      failed: "destructive", 
      pending: "secondary"
    } as const;
    
    return (
      <Badge variant={variants[status as keyof typeof variants] || "secondary"}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const filteredInvoices = invoiceHistory.filter(invoice =>
    invoice.description.toLowerCase().includes(billingSearchTerm.toLowerCase()) ||
    invoice.id.toLowerCase().includes(billingSearchTerm.toLowerCase())
  );

  // Billing handlers
  const handleUpgradeSubscription = () => {
    navigate('/profile?tab=billing');
  };

  const handleUpdateBilling = () => {
    setShowBillingDialog(true);
  };

  const handleSaveBilling = async () => {
    setIsSavingBilling(true);

    try {
      // Update user metadata in Supabase
      const { error } = await supabase.auth.updateUser({
        data: {
          full_name: billingForm.name,
          company_name: billingForm.company,
          address: billingForm.address,
          city: billingForm.city,
          state: billingForm.state,
          zip_code: billingForm.zipCode,
          country: billingForm.country
        }
      });

      if (error) throw error;

      setBillingInfo(billingForm);
      setShowBillingDialog(false);
      toast({
        title: "Billing information updated",
        description: "Your billing information has been successfully updated.",
      });
    } catch (error) {
      console.error('[ProfilePage] Error saving billing:', error);
      toast({
        title: "Error",
        description: "Failed to update billing information. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSavingBilling(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    try {
      // ✅ Use real subscription data from SubscriptionContext
      if (subscriptionUsage?.isTrialActive || !subscriptionUsage?.subscribed) {
        // User is on trial or not subscribed - redirect to billing page
        navigate('/profile?tab=billing');
        return;
      }

      // ✅ User has active subscription - open customer portal via backend API
      try {
        const response = await apiClient.get<{ ok: boolean; url: string }>(
          API_ROUTES.BILLING_CUSTOMER_PORTAL
        );

        if (!response.ok || !response.url) {
          throw new Error('Failed to get customer portal URL');
        }

        window.open(response.url, '_blank');
      } catch (portalError) {
        console.error('[ProfilePage] Customer portal error:', portalError);
        toast({
          title: "Error",
          description: "Failed to open payment portal. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('[ProfilePage] Payment method error:', error);
    }
  };

  const handleCancelSubscription = () => {
    toast({
      title: "Cancel subscription",
      description: "Cancellation process would begin here...",
      variant: "destructive",
    });
  };

  // ✅ REMOVED: Notification preferences now auto-save via SettingsContext setters
  // Each Switch component directly calls the context setter which saves to backend API

  const handleSave = async () => {
    // Validate first and last name are not empty
    if (!personalDetails.firstName?.trim()) {
      toast({
        title: "Error",
        description: "First name cannot be empty.",
        variant: "destructive",
      });
      return;
    }
    if (!personalDetails.lastName?.trim()) {
      toast({
        title: "Error",
        description: "Last name cannot be empty.",
        variant: "destructive",
      });
      return;
    }

    setIsSavingProfile(true);

    try {
      let finalAvatarUrl = avatarUrl;

      // Upload avatar if a new file is pending
      if (pendingAvatarFile) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        // Create unique filename
        const fileExt = pendingAvatarFile.name.split('.').pop();
        const fileName = `avatar-${Date.now()}.${fileExt}`;
        const filePath = `${user.id}/${fileName}`;

        // Upload file to storage
        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(filePath, pendingAvatarFile, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('avatars')
          .getPublicUrl(filePath);

        finalAvatarUrl = publicUrl;

        // Clean up the preview URL
        if (pendingAvatarPreview) {
          URL.revokeObjectURL(pendingAvatarPreview);
        }
      }

      // Combine first and last name
      const fullName = `${personalDetails.firstName.trim()} ${personalDetails.lastName.trim()}`.trim();

      // Parse location into city and state
      const locationParts = personalDetails.location ? personalDetails.location.split(',').map(s => s.trim()) : [];
      const city = locationParts[0] || null;
      const state = locationParts[1] || null;

      // Update profile via backend API
      const response = await apiClient.put<{ ok: boolean; profile: any; message: string }>(
        API_ROUTES.USER_PROFILE,
        {
          name: fullName || null,
          first_name: personalDetails.firstName || null,
          last_name: personalDetails.lastName || null,
          phone: personalDetails.phoneNumber || null,
          role_job_title: personalDetails.roleJobTitle || null,
          website: personalDetails.website || null,
          company_name: personalDetails.companyName || null,
          timezone: personalDetails.timezone || null,
          city: city,
          state: state,
          avatar_url: finalAvatarUrl || null,
        }
      );

      if (!response.ok) {
        throw new Error(response.message || 'Failed to update profile');
      }

      // Update timezone in settings context
      setUserTimezone(personalDetails.timezone);
      setInitialPersonalDetails(personalDetails);
      setInitialAvatarUrl(finalAvatarUrl);
      setAvatarUrl(finalAvatarUrl);
      setPendingAvatarFile(null);
      setPendingAvatarPreview("");

      // Refresh user data from backend to ensure UI shows latest data
      await refreshUserData();

      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated.",
      });
    } catch (error: any) {
      console.error('[ProfilePage] Error saving profile:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const isPasswordStrong = (password: string) => {
    const hasLower = /[a-z]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*()_\-+=\[\]{};':"\\|,.<>/?`~]/.test(password);
    return hasLower && hasUpper && hasNumber && hasSpecial;
  };

  const handlePasswordChange = async () => {
    // Validate all fields are filled
    if (!security.currentPassword || !security.newPassword || !security.confirmPassword) {
      toast({
        title: "Error",
        description: "Please fill in all password fields.",
        variant: "destructive",
      });
      return;
    }

    // Validate new passwords match
    if (security.newPassword !== security.confirmPassword) {
      toast({
        title: "Error",
        description: "New passwords don't match.",
        variant: "destructive",
      });
      return;
    }

    // Validate new password is different from current
    if (security.currentPassword === security.newPassword) {
      toast({
        title: "Error",
        description: "New password must be different from your current password.",
        variant: "destructive",
      });
      return;
    }

    // Validate password strength using unified helper
    const passwordError = validatePassword(security.newPassword);
    if (passwordError) {
      toast({
        title: "Invalid Password",
        description: passwordError,
        variant: "destructive",
      });
      return;
    }

    setIsUpdatingPassword(true);
    try {
      // First verify current password by attempting to sign in
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        throw new Error("User not found");
      }

      // Test current password by trying to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: security.currentPassword,
      });

      if (signInError) {
        toast({
          title: "Error",
          description: "Current password is incorrect.",
          variant: "destructive",
        });
        return;
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: security.newPassword
      });

      if (updateError) {
        throw updateError;
      }

      // Refresh session so tokens align with the new password
      await supabase.auth.refreshSession();

      toast({
        title: "Password updated",
        description: "Your password has been successfully changed.",
      });
      
      setSecurity(prev => ({
        ...prev,
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      }));

    } catch (error) {
      console.error('Password update error:', error);
      toast({
        title: "Error",
        description: "Failed to update password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const activeTab = searchParams.get('tab') || "personal";

  const navItems = [
    { id: "personal", label: "Profile", icon: User },
    { id: "invites", label: "Invites", icon: Mail, badge: pendingInvitesCount },
    { id: "team", label: "Team Management", icon: Users },
    { id: "billing", label: "Billing", icon: CreditCard },
    { id: "security", label: "Security", icon: Shield },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "integrations", label: "Integrations", icon: Plug },
  ];

  const handleNavClick = (tabId: string) => {
    setSearchParams({ tab: tabId });
  };

  const handleLogout = async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) console.warn("⚠️ Session rehydrate error:", sessionError);

      const { error } = await supabase.auth.signOut();

      if (error) {
        console.error('Logout error:', error);
      } else {
        // Clear all localStorage except onboarding flag
        const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
        localStorage.clear();
        if (hasSeenOnboarding) {
          localStorage.setItem('hasSeenOnboarding', hasSeenOnboarding);
        }

        // Go to landing page (like mobile app goes to login screen)
        navigate('/', { state: { fromLogout: true } });
      }
    } catch (error) {
      console.error('Unexpected logout error:', error);
      const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
      localStorage.clear();
      if (hasSeenOnboarding) {
        localStorage.setItem('hasSeenOnboarding', hasSeenOnboarding);
      }
      navigate('/', { state: { fromLogout: true } });
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-50 flex items-center justify-center p-4">
      {/* Centered Card Container */}
      <Card className="w-full max-w-6xl h-[calc(100vh-8rem)] flex flex-col shadow-lg">
        {/* Card Header */}
        <CardHeader className="border-b">
          <CardTitle className="text-2xl">Account Settings</CardTitle>
          <CardDescription>Manage your profile and preferences</CardDescription>
        </CardHeader>

        {/* Card Content: Navigation + Content Side by Side */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Navigation */}
          <div className="w-64 border-r bg-gray-50/50 flex flex-col">
            <nav className="flex-1 p-4 overflow-y-auto">
              <ul className="space-y-1">
                {navItems.map((item) => {
                  const isActive = activeTab === item.id;

                  return (
                    <li key={item.id}>
                      <button
                        onClick={() => handleNavClick(item.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left ${
                          isActive
                            ? 'bg-primary text-white font-medium'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <span className="flex-1">{item.label}</span>
                        {item.badge && item.badge > 0 && (
                          <Badge variant="destructive" className="ml-auto">
                            {item.badge}
                          </Badge>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

          </div>

          {/* Right Content Area */}
          <div className="flex-1 overflow-y-auto">
            <Tabs value={activeTab} className="h-full">

        {/* Personal Details Tab */}
        <TabsContent value="personal" className="p-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>
                Update your personal information and profile details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Profile Picture */}
              <div className="flex items-center gap-6">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={avatarUrl} alt="Profile" />
                  <AvatarFallback className="text-lg">{getUserInitials()}</AvatarFallback>
                </Avatar>
                <div className="space-y-2">
                  <div>
                    <input
                      type="file"
                      id="avatar-upload"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                      className="hidden"
                    />
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex items-center gap-2"
                      onClick={() => document.getElementById('avatar-upload')?.click()}
                      disabled={isUploadingAvatar}
                    >
                      <Camera className="h-4 w-4" />
                      {isUploadingAvatar ? "Uploading..." : "Change Photo"}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    JPG, GIF or PNG. 5MB max.
                  </p>
                </div>
              </div>

              <Separator />

              {/* Personal Information Form */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="companyName" className="flex items-center gap-2">
                    Company Name
                    {!isCompanyAdmin && <Lock className="h-3 w-3 text-muted-foreground" />}
                  </Label>
                  <Input
                    id="companyName"
                    value={personalDetails.companyName}
                    onChange={(e) => setPersonalDetails({...personalDetails, companyName: e.target.value})}
                    disabled={!isCompanyAdmin}
                    readOnly={!isCompanyAdmin}
                    className={!isCompanyAdmin ? "bg-muted cursor-not-allowed text-foreground" : ""}
                  />
                  {!isCompanyAdmin && (
                    <p className="text-xs text-muted-foreground">
                      Company name cannot be changed. Only company admins can edit this field.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={personalDetails.firstName}
                    onChange={(e) => setPersonalDetails(prev => ({ ...prev, firstName: e.target.value }))}
                    placeholder="Enter first name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={personalDetails.lastName}
                    onChange={(e) => setPersonalDetails(prev => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Enter last name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Phone Number</Label>
                  <Input
                    id="phoneNumber"
                    value={personalDetails.phoneNumber}
                    onChange={(e) => setPersonalDetails(prev => ({ ...prev, phoneNumber: e.target.value }))}
                  />
                </div>


                <div className="space-y-2">
                  <Label htmlFor="roleJobTitle">Role/Job Title</Label>
                  <Input
                    id="roleJobTitle"
                    value={personalDetails.roleJobTitle}
                    onChange={(e) => setPersonalDetails(prev => ({ ...prev, roleJobTitle: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={personalDetails.location}
                    onChange={(e) => setPersonalDetails(prev => ({ ...prev, location: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select
                    value={personalDetails.timezone}
                    onValueChange={(value) => setPersonalDetails(prev => ({ ...prev, timezone: value }))}
                  >
                    <SelectTrigger id="timezone">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <SelectItem key={tz} value={tz}>
                          {tz}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    type="url"
                    value={personalDetails.website}
                    onChange={(e) => setPersonalDetails(prev => ({ ...prev, website: e.target.value }))}
                  />
                </div>

              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={handleSave} 
                  className={`transition-all duration-300 ${
                    hasPersonalDetailsChanged() ? 'shadow-lg ring-2 ring-primary/20 brightness-110' : ''
                  }`}
                  variant={hasPersonalDetailsChanged() ? "default" : "outline"}
                  style={hasPersonalDetailsChanged() ? 
                    { backgroundColor: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' } : 
                    { borderColor: 'hsl(var(--primary))', color: 'hsl(var(--primary))' }
                  }
                >
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invites Tab */}
        <TabsContent value="invites" className="p-6">
          <InvitesPage />
        </TabsContent>

        {/* Team Management Tab */}
        <TabsContent value="team" className="p-6">
          <TeamManagementPage />
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="p-0">
          <BillingPage />
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="p-6 space-y-6">
          {!isGoogleAuth && (
            <Card>
              <CardHeader>
                <CardTitle>Change Password</CardTitle>
                <CardDescription>
                  Update your password to keep your account secure
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="currentPassword"
                    type={showPassword ? "text" : "password"}
                    value={security.currentPassword}
                    onChange={(e) => setSecurity(prev => ({ ...prev, currentPassword: e.target.value }))}
                    className="pl-9 pr-9"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1 h-7 w-7 p-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    value={security.newPassword}
                    onChange={(e) => setSecurity(prev => ({ ...prev, newPassword: e.target.value }))}
                    className="pl-9 pr-9"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1 h-7 w-7 p-0"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                 </div>
                 {security.newPassword && (
                   <div className="text-sm space-y-1">
                     <div className={`flex items-center gap-1 ${security.newPassword.length >= 6 ? 'text-green-600' : 'text-red-600'}`}>
                       {security.newPassword.length >= 6 ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                       <span>At least 6 characters</span>
                     </div>
                     <div className={`flex items-center gap-1 ${/[a-z]/.test(security.newPassword) ? 'text-green-600' : 'text-red-600'}`}>
                       {/[a-z]/.test(security.newPassword) ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                       <span>One lowercase letter</span>
                     </div>
                     <div className={`flex items-center gap-1 ${/[A-Z]/.test(security.newPassword) ? 'text-green-600' : 'text-red-600'}`}>
                       {/[A-Z]/.test(security.newPassword) ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                       <span>One uppercase letter</span>
                     </div>
                     <div className={`flex items-center gap-1 ${/\d/.test(security.newPassword) ? 'text-green-600' : 'text-red-600'}`}>
                       {/\d/.test(security.newPassword) ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                       <span>One number</span>
                     </div>
                     <div className={`flex items-center gap-1 ${/[!@#$%^&*()_\-+=\[\]{};':"\\|,.<>/?`~]/.test(security.newPassword) ? 'text-green-600' : 'text-red-600'}`}>
                       {/[!@#$%^&*()_\-+=\[\]{};':"\\|,.<>/?`~]/.test(security.newPassword) ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                       <span>One special character</span>
                     </div>
                   </div>
                 )}
              </div>

               <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={security.confirmPassword}
                    onChange={(e) => setSecurity(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="pl-9"
                  />
                </div>
                {security.newPassword && security.confirmPassword && (
                  <div className="flex items-center gap-2 text-sm">
                    {security.newPassword === security.confirmPassword ? (
                      <div className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="h-4 w-4" />
                        <span>Passwords match</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-red-600">
                        <XCircle className="h-4 w-4" />
                        <span>Passwords don't match</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Button onClick={handlePasswordChange} className="w-full" disabled={isUpdatingPassword}>
                {isUpdatingPassword ? "Updating..." : "Update Password"}
              </Button>
            </CardContent>
          </Card>
          )}

          {/* Show message for Google auth users */}
          {isGoogleAuth && (
            <Card>
              <CardHeader>
                <CardTitle>Password Management</CardTitle>
                <CardDescription>
                  You signed in with Google
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Your account is secured through Google. You can manage your password through your Google account settings.
                </p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Login Notifications</CardTitle>
              <CardDescription>
                Get notified when someone logs into your account from a new device
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="font-medium">Email Notifications</p>
                  <p className="text-sm text-muted-foreground">
                    Receive email alerts for logins from unknown devices
                  </p>
                </div>
                <Switch
                  checked={security.loginNotifications}
                  onCheckedChange={(checked) => setSecurity(prev => ({ ...prev, loginNotifications: checked }))}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="p-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Communication Preferences</CardTitle>
              <CardDescription>
                Choose how you want to receive notifications and updates
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                 <div className="flex items-center justify-between">
                   <div>
                     <p className="font-medium">Email Notifications</p>
                     <p className="text-sm text-muted-foreground">Receive notifications via email</p>
                   </div>
                   <Switch
                     checked={emailNotifications}
                     onCheckedChange={setEmailNotifications}
                   />
                 </div>

                 <div className="flex items-center justify-between">
                   <div>
                     <p className="font-medium">Push Notifications</p>
                     <p className="text-sm text-muted-foreground">Receive notifications in your browser</p>
                   </div>
                   <Switch
                     checked={pushNotifications}
                     onCheckedChange={setPushNotifications}
                   />
                 </div>

                 <div className="flex items-center justify-between">
                   <div>
                     <p className="font-medium">SMS Notifications</p>
                     <p className="text-sm text-muted-foreground">Receive notifications via text message</p>
                   </div>
                   <Switch
                     checked={smsNotifications}
                     onCheckedChange={setSmsNotifications}
                   />
                 </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-medium">Content Preferences</h4>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Product Updates</p>
                    <p className="text-sm text-muted-foreground">Stay informed about new features</p>
                  </div>
                  <Switch
                    checked={productUpdates}
                    onCheckedChange={setProductUpdates}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Security Alerts</p>
                    <p className="text-sm text-muted-foreground">Important security notifications</p>
                  </div>
                  <Switch
                    checked={securityAlerts}
                    onCheckedChange={setSecurityAlerts}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Marketing Emails</p>
                    <p className="text-sm text-muted-foreground">Tips, tutorials, and promotional content</p>
                  </div>
                  <Switch
                    checked={marketingEmails}
                    onCheckedChange={setMarketingEmails}
                  />
                </div>
              </div>

              {/* ✅ Auto-save enabled - changes are saved immediately when toggling switches */}
              <p className="text-sm text-muted-foreground">
                Changes are saved automatically
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="p-6 space-y-4">
          <div>
            <h3 className="text-lg font-semibold">Integrations</h3>
            <p className="text-sm text-muted-foreground">Connect third-party services to enhance your workflow.</p>
          </div>
          <GoogleCalendarIntegration />
        </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Message Detail Dialog */}
        <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                {selectedMessage?.subject}
              </DialogTitle>
            </DialogHeader>

            {selectedMessage && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>From: {selectedMessage.from}</span>
                  <span>•</span>
                  <span>{formatDate(selectedMessage.timestamp)}</span>
                  {selectedMessage.ticketId && (
                    <>
                      <span>•</span>
                      <span className="font-mono">{selectedMessage.ticketId}</span>
                    </>
                  )}
                </div>

                <div className="prose max-w-none">
                  <p className="text-foreground leading-relaxed">
                    {selectedMessage.content}
                  </p>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Reply</h4>
                  <Textarea
                    placeholder="Type your reply here..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    className="min-h-[100px] mb-3"
                  />
                  <div className="flex gap-2">
                    <Button onClick={handleReply} disabled={!replyText.trim()}>
                      <Reply className="h-4 w-4 mr-2" />
                      Send Reply
                    </Button>
                    <Button variant="outline" onClick={() => setSelectedMessage(null)}>
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </Card>
    </div>
  );
}
